import { BreachPathDetailsView } from '../view/BreachPathDetailsView';
import { templateBreachPathDetails } from '../view/template';
import { BreachPathDetailsRepository } from '../repository/BreachPathDetailsRepository';
import { BaseWebComponentController } from '../../../framework/BaseWebComponentController';
import { BreachPathDetailsWebComponent } from '..';
import { BreachPathDetailsResourceSideMenuWebComponent } from '../../breachPathDetailsResourceSideMenu/component/BreachPathDetailsResourceSideMenuWebComponent';
import { BreachPathDetailsTransitionSideMenuWebComponent } from '../../breachPathDetailsTransitionSideMenu/component/BreachPathDetailsTransitionSideMenuWebComponent';
import { BreachPathDetailsMetadataSideMenuWebComponment } from '../../breachPathDetailsMetadataSideMenu/component/BreachPathDetailsMetadataSideMenuComponent';
import { BreachPathIssue, BreachPathNode, BreachPathPath } from '../types';
import { GridEvents } from 'apps/webcomponents/src/grid/types/common';
import { BreachPathEvents } from '../../common/types';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { ColumnKeys as DevicesColumnKeys } from 'apps/webcomponents/src/riskManagement/devicesApp/types';

export class BreachPathDetailsController extends BaseWebComponentController {
  public component: BreachPathDetailsWebComponent;
  public dataRepository: BreachPathDetailsRepository;
  public view: BreachPathDetailsView;
  public viewConstructor: typeof BreachPathDetailsView;
  public dataRepositoryConstructor: typeof BreachPathDetailsRepository;

  constructor(component: BreachPathDetailsWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: BreachPathDetailsView,
      viewTemplate: templateBreachPathDetails,
      dataRepositoryConstructor: BreachPathDetailsRepository,
    });
  }

  public async render() {
    const isBreachPathIssueIdChanged = this.view.breachPathIssueId !== this.component.breachPathIssueId;
    
    // Inject base web component properties to view
    this.view.breachPathIssueId = this.component.breachPathIssueId;
    this.view.tabName = this.component.tabName;

    if (isBreachPathIssueIdChanged) {
      // Reset breachPathIssue data
      this.view.breachPathIssue = null;

      // Set loading animation on grids
      this.view.dom.$mainGrid.style.display = 'none';
      this.view.dom.$mainGrid.previousElementSibling.style.display = 'block';
      this.view.dom.$otherGrid.style.display = 'none';
      this.view.dom.$otherGrid.previousElementSibling.style.display = 'block';

      // Reset breadcrumbs
      this.view.dom.$pageNavigationHeader.items = []
    }

    // Hide sidemenu on tab change
    this.closeSideMenu();

    await super.render();
  }

  public computeEventListeners() {
    return [
      {
        element: this.view.dom.$graphSlot,
        event: 'nodeClicked',
        callback: this.showResourcesSideMenu,
      },
      {
        element: this.view.dom.$graphSlot,
        event: 'pathClicked',
        callback: this.showTransitionSideMenu,
      },
      {
        element: this.view.dom.$mainGrid,
        event: GridEvents.ROW_CLICKED,
        callback: this.handleRowClicked,
      },
      {
        element: this.view.dom.$otherGrid,
        event: GridEvents.ROW_CLICKED,
        callback: this.handleRowClicked,
      },
      {
        element: this.view.dom.$page,
        event: 'graphDataLoaded',
        callback: this.handleGraphDataLoaded,
      },
      {
        element: this.view.dom.$sideMenu,
        event: BreachPathEvents.NAV_VIEW_RESOURCES_PAGE,
        callback: this.handleViewResourceButtonClick,
      },
      {
        element: this.view.dom.$sideMenu,
        event: BreachPathEvents.VIEW_METADATA_RESOURCES_SIDEMENU,
        callback: this.handleViewMetadataButtonClick,
      },
      {
        element: this.view.dom.$metadataSideMenu,
        event: BreachPathEvents.GO_BACK_METADATA_RESOURCES_SIDEMENU,
        callback: this.handleMetadataSideMenuBackButtonClick,
      },
      {
        element: this.view.dom.$backButton,
        event: BreachPathEvents.GO_BACK_DETAILS_PAGE,
        callback: this.handleDetailsPageBackButtonClick,
      },
    ];
  }

  private async showResourcesSideMenu(event: { detail: { data: BreachPathNode } }) {
    const data = event.detail.data;

    const component = new BreachPathDetailsResourceSideMenuWebComponent();
    component.loading = true;
    this.view.dom.$sideMenu.setMenuContent(component);
    this.view.dom.$sideMenu.openSideMenu();
    // compute data for sidemenu
    // ...
    component.data = data;
    component.loading = false;
  }

  private async showTransitionSideMenu(event: { detail: { data: BreachPathPath } }) {
    const data = event.detail.data;

    const component = new BreachPathDetailsTransitionSideMenuWebComponent();
    component.loading = true;
    this.view.dom.$sideMenu.setMenuContent(component);
    this.view.dom.$sideMenu.openSideMenu();
    // compute data for sidemenu
    // ...
    component.data = data;
    component.loading = false;
  }

  private removeHighlightGridRow() {
    if (this.view.dom.$mainGrid.gridLoaded) {
      this.view.dom.$mainGrid.controller.view.removeHighlightGridRows();
    }
    if (this.view.dom.$otherGrid.gridLoaded) {
      this.view.dom.$otherGrid.controller.view.removeHighlightGridRows();
    }
  }

  private closeSideMenu(fireEvent = false) {
    this.view.dom.$sideMenu.closeSideMenu(fireEvent);
    this.view.dom.$sideMenu.setMenuContent(null);
    this.removeHighlightGridRow();
  }

  async handleRowClicked(event: CustomEvent<{ rowDetails: any }>) {
    if (!event.detail.rowDetails) {
      // header was clicked, so we do nothing
      return;
    }

    const component = new BreachPathDetailsResourceSideMenuWebComponent();
    component.loading = true;
    this.view.dom.$sideMenu.setMenuContent(component);
    this.view.dom.$sideMenu.openSideMenu();
    component.data = event.detail.rowDetails;
    component.loading = false;
  }

  private waitForBreachPathIssueData() {
    return new Promise<void>((resolve) => {
      const checkDataReadiness = () => {
        if (this.view.breachPathIssue) {
          resolve();
        } else {
          setTimeout(checkDataReadiness, 500);
        }
      };
      checkDataReadiness();
    });
  }

  private async handleGraphDataLoaded(data) {
    await this.waitForBreachPathIssueData();

    const resourceIdToNodeMap: Map<string, BreachPathNode> = data.detail.data.nodes.reduce((acc, node) => {
      try {
        const { resource_id } = JSON.parse(node.metadata);
        acc.set(resource_id, node);
      } catch {}
      return acc;
    }, new Map());

    const mainGridData = [(this.view.breachPathIssue as BreachPathIssue).mainResource].map(({ resourceId }) => {
      return (resourceIdToNodeMap.get(resourceId) ?? {}) as BreachPathNode;
    });
    const otherGridData = (this.view.breachPathIssue as BreachPathIssue).affectedResources.map(({ resourceId }) => {
      return (resourceIdToNodeMap.get(resourceId) ?? {}) as BreachPathNode;
    });

    this.view.buildGrid(this.view.dom.$mainGrid, mainGridData);
    this.view.buildGrid(this.view.dom.$otherGrid, otherGridData);
  }

  private handleViewResourceButtonClick(event: CustomEvent) {
    const { companyId, resourceId, operatingSystem } = event.detail;
    const params = {
      filters: [
        {
          [DevicesColumnKeys.COMPANY]: { exactMatch: companyId },
        },
        {
          [DevicesColumnKeys.OS]: {
            in: [operatingSystem],
          },
        },
        {
          [DevicesColumnKeys.RESOURCE_ID]: {
            complexEnumFilter: [resourceId],
          },
        },
      ],
    };

    NavigationHelper.navigateTo('/risk-management-resources', params);
  }

  private handleViewMetadataButtonClick(event: CustomEvent) {
    const component = new BreachPathDetailsMetadataSideMenuWebComponment();
    component.data = event.detail.data;
    this.view.dom.$metadataSideMenu.setMenuContent(component);
    this.view.dom.$metadataSideMenu.openSideMenu();
  }

  private handleMetadataSideMenuBackButtonClick(event: CustomEvent) {
    this.view.dom.$metadataSideMenu.closeSideMenu();
    this.view.dom.$metadataSideMenu.setMenuContent(null);
  }

  private handleDetailsPageBackButtonClick(event: CustomEvent) {
    NavigationHelper.navigateTo(`/risk-management-breach-path/${this.view.breachPathIssueId}`);
  }
}
